import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  TRIGGER_EVENT_CATALOG,
  type PayloadMatch,
  type PayloadMatchOperator,
  type TriggerSpec,
  type WorkflowConfig,
} from '@reef/schema';
import { api, type WebhookEndpointSummary } from '../api.ts';
import { Icon } from './Icon.tsx';

/**
 * Everything that can start this workflow.
 *
 * Two ways to narrow a trigger, offered side by side because they answer
 * different questions. The event list is "which *kind* of thing happened" — a
 * closed set per source, so it is a dropdown and a typo is impossible. The
 * conditions are "which *particular* things" — open-ended, because it is a path
 * into somebody else's payload, so it is text with a worked example next to it.
 *
 * Webhook triggers get a third panel, because they are the only source whose
 * setup reef itself owns: the token is minted here and shown exactly once.
 */
export function TriggerEditor({
  workflow,
  editable,
  onChange,
}: {
  workflow: WorkflowConfig;
  editable: boolean;
  onChange(triggers: TriggerSpec[]): void;
}) {
  const triggers = workflow.triggers;

  const patch = (index: number, next: TriggerSpec) =>
    onChange(triggers.map((trigger, position) => (position === index ? next : trigger)));

  return (
    <div className="card">
      <h2>Triggers</h2>
      <p className="hint" style={{ marginTop: 0 }}>
        What starts a run. Add one for each thing that should start this workflow; a workflow
        with none can still be run by hand from this screen and through the API.
      </p>

      <div className="stack">
        {triggers.map((trigger, index) => (
          <TriggerRow
            key={index}
            trigger={trigger}
            workflowId={workflow.id}
            editable={editable}
            onChange={(next) => patch(index, next)}
            onRemove={() => onChange(triggers.filter((_, position) => position !== index))}
          />
        ))}

        {triggers.length === 0 && (
          <p className="hint">No triggers yet — this workflow only runs when someone asks it to.</p>
        )}

        {editable && (
          /*
            A menu of sources, each with the sentence that says when it fires,
            rather than a row of status pills. The pills carried the dot every
            status pill carries, and beside a plus sign it read as a control
            with two halves — people asked what the circle was for. An option
            here is one button, and the sentence under its name is what tells
            somebody choosing between Webhook and Slack which one they want.
          */
          <div className="trigger-add">
            <span className="trigger-add-title">Add a trigger</span>
            <div className="trigger-add-grid">
              {(['manual', 'schedule', 'webhook', 'linear', 'github', 'jira', 'slack'] as const)
                // A workflow has one Run button, so a second manual trigger adds
                // nothing and can disagree with the first about who may press it.
                // The others are all narrowings, and several of those is the point.
                .filter(
                  (type) =>
                    type !== 'manual' || !triggers.some((existing) => existing.type === 'manual'),
                )
                .map((type) => (
                  <button
                    key={type}
                    type="button"
                    className="trigger-add-option"
                    aria-label={`Add ${SOURCE_LABELS[type]} trigger`}
                    onClick={() => onChange([...triggers, blankTrigger(type)])}
                  >
                    <Icon name="plus" />
                    <span>
                      <strong>{SOURCE_LABELS[type]}</strong>
                      <span className="hint">{SOURCE_BLURBS[type]}</span>
                    </span>
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- one trigger

function TriggerRow({
  trigger,
  workflowId,
  editable,
  onChange,
  onRemove,
}: {
  trigger: TriggerSpec;
  workflowId: string;
  editable: boolean;
  onChange(next: TriggerSpec): void;
  onRemove(): void;
}) {
  return (
    <div className="trigger-row">
      <div className="trigger-head">
        <div>
          <strong>{SOURCE_LABELS[trigger.type]}</strong>
          <span className="hint">{SOURCE_BLURBS[trigger.type]}</span>
        </div>
        {editable && (
          <button className="ghost" onClick={onRemove} aria-label={`Remove ${trigger.type} trigger`}>
            Remove
          </button>
        )}
      </div>

      {trigger.type === 'manual' && (
        <label className="row" style={{ gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={trigger.triggerFromEveryone}
            disabled={!editable}
            onChange={(event) => onChange({ ...trigger, triggerFromEveryone: event.target.checked })}
          />
          <span>
            Anyone who can see this workflow may run it
            <span className="hint" style={{ display: 'block' }}>
              Off means only people with edit access can start a run.
            </span>
          </span>
        </label>
      )}

      {trigger.type === 'schedule' && (
        <div className="field">
          <label htmlFor="trigger-cron">Cron expression (UTC)</label>
          <input
            id="trigger-cron"
            value={trigger.cron ?? ''}
            placeholder="0 9 * * 1-5"
            disabled={!editable}
            onChange={(event) => onChange({ ...trigger, cron: event.target.value || undefined })}
          />
          <span className="hint">
            Five fields, evaluated in UTC — <code>0 9 * * 1-5</code> is 09:00 UTC on weekdays. A
            scheduled run has no acting person, so it uses workspace credentials; a step pinned to{' '}
            <code>actor: 'user'</code> will stop it.
          </span>
        </div>
      )}

      {trigger.type === 'webhook' && (
        <>
          <FreeTextEvents
            events={trigger.events ?? []}
            editable={editable}
            onChange={(events) => onChange({ ...trigger, events })}
          />
          <WebhookEndpoints workflowId={workflowId} editable={editable} />
        </>
      )}

      {(trigger.type === 'linear' ||
        trigger.type === 'github' ||
        trigger.type === 'jira' ||
        trigger.type === 'slack') && (
        <EventPicker
          source={trigger.type}
          selected={trigger.events}
          editable={editable}
          onChange={(events) => onChange({ ...trigger, events } as TriggerSpec)}
        />
      )}

      {trigger.type === 'linear' && (
        <div className="row wrap">
          <ListField
            label="Labels"
            values={trigger.labels}
            editable={editable}
            onChange={(labels) => onChange({ ...trigger, labels })}
          />
          <LinearTeamPicker
            values={trigger.teams}
            editable={editable}
            onChange={(teams) => onChange({ ...trigger, teams })}
          />
          <ListField
            label="Projects"
            values={trigger.projects}
            editable={editable}
            onChange={(projects) => onChange({ ...trigger, projects })}
          />
          <div className="field" style={{ flex: 1, minWidth: '10rem' }}>
            <label htmlFor="linear-state">State</label>
            <input
              id="linear-state"
              value={trigger.state ?? ''}
              placeholder="triage"
              disabled={!editable}
              onChange={(event) => onChange({ ...trigger, state: event.target.value || undefined })}
            />
          </div>
        </div>
      )}

      {trigger.type === 'github' && (
        <div className="row wrap">
          <ListField
            label="Repositories"
            values={trigger.repos}
            placeholder="acme/web"
            editable={editable}
            onChange={(repos) => onChange({ ...trigger, repos })}
          />
          <ListField
            label="Branches"
            values={trigger.branches}
            placeholder="main"
            editable={editable}
            onChange={(branches) => onChange({ ...trigger, branches })}
          />
          <ListField
            label="Labels"
            values={trigger.labels}
            editable={editable}
            onChange={(labels) => onChange({ ...trigger, labels })}
          />
          <ListField
            label="Assignees"
            values={trigger.assignees ?? []}
            placeholder="reef-bot"
            editable={editable}
            onChange={(assignees) =>
              // Absent rather than empty, matching the schema: a trigger nobody
              // narrowed by assignee should add nothing to the committed
              // document.
              onChange({ ...trigger, assignees: assignees.length ? assignees : undefined })
            }
          />
        </div>
      )}

      {trigger.type === 'jira' && (
        <div className="row wrap">
          <ListField
            label="Projects"
            values={trigger.projects}
            placeholder="ENG"
            editable={editable}
            onChange={(projects) => onChange({ ...trigger, projects })}
          />
          <ListField
            label="Labels"
            values={trigger.labels}
            editable={editable}
            onChange={(labels) => onChange({ ...trigger, labels })}
          />
          <ListField
            label="Issue types"
            values={trigger.issueTypes}
            placeholder="Bug"
            editable={editable}
            onChange={(issueTypes) => onChange({ ...trigger, issueTypes })}
          />
          <ListField
            label="Assignees"
            values={trigger.assignees}
            placeholder="account id, email or display name"
            editable={editable}
            onChange={(assignees) => onChange({ ...trigger, assignees })}
          />
          <div className="field" style={{ flex: 1, minWidth: '10rem' }}>
            <label htmlFor="jira-status">Status</label>
            <input
              id="jira-status"
              value={trigger.status ?? ''}
              placeholder="In Progress"
              disabled={!editable}
              onChange={(event) => onChange({ ...trigger, status: event.target.value || undefined })}
            />
          </div>
        </div>
      )}

      {trigger.type === 'slack' && (
        <>
          <div className="row wrap">
            <SlackChannelPicker
              values={trigger.channels}
              editable={editable}
              onChange={(channels) => onChange({ ...trigger, channels })}
            />
            {/*
              Only offered with "Reaction added" on, because that is the only
              event it narrows: reef matches the emoji against the reaction on
              the message, and an emoji filter on a mention trigger matches
              nothing and looks like a setting. It stays visible while it holds
              a value the events no longer use, so the value can be seen and
              taken out rather than silently committed.
            */}
            {(trigger.events.includes('reaction_added') || (trigger.emoji ?? []).length > 0) && (
              <EmojiPicker
                values={trigger.emoji ?? []}
                applies={trigger.events.includes('reaction_added')}
                editable={editable}
                onChange={(emoji) => onChange({ ...trigger, emoji })}
              />
            )}
          </div>
          <p className="trigger-sentence">{describeSlack(trigger.events, trigger.channels, trigger.emoji ?? [])}</p>
        </>
      )}

      {'match' in trigger && (
        <MatchEditor
          conditions={trigger.match ?? []}
          mode={trigger.matchMode ?? 'all'}
          source={trigger.type}
          editable={editable}
          onChange={(match, matchMode) =>
            onChange({
              ...trigger,
              // Absent rather than empty: an unfiltered trigger should add
              // nothing to the committed document.
              match: match.length ? match : undefined,
              matchMode: match.length && matchMode === 'any' ? 'any' : undefined,
            } as TriggerSpec)
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- the events

function EventPicker({
  source,
  selected,
  editable,
  onChange,
}: {
  source: string;
  selected: readonly string[];
  editable: boolean;
  onChange(events: string[]): void;
}) {
  const options = TRIGGER_EVENT_CATALOG[source] ?? [];

  return (
    <div className="field">
      <label>Events</label>
      <div className="row wrap">
        {options.map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={option.value}
              className={`pill ${active ? 'accent' : ''}`}
              title={option.description}
              disabled={!editable}
              aria-pressed={active}
              onClick={() =>
                onChange(
                  active
                    ? selected.filter((event) => event !== option.value)
                    : [...selected, option.value],
                )
              }
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {selected.length === 0 && (
        <span className="hint">Pick at least one — a trigger with no events subscribes to nothing.</span>
      )}
    </div>
  );
}

/**
 * Webhook events are free text, because the sender names them.
 *
 * Offered as suggestions rather than a closed list: reef reads the name out of
 * `X-Reef-Event` or an `event`/`type` field, and whatever is in there is what
 * you have to match.
 */
function FreeTextEvents({
  events,
  editable,
  onChange,
}: {
  events: readonly string[];
  editable: boolean;
  onChange(events: string[]): void;
}) {
  return (
    <div className="field">
      <label htmlFor="webhook-events">Events</label>
      <input
        id="webhook-events"
        value={events.join(', ')}
        placeholder="leave empty to accept every delivery"
        disabled={!editable}
        onChange={(event) => onChange(splitList(event.target.value))}
      />
      <span className="hint">
        Comma-separated. Reef takes the event name from the <code>X-Reef-Event</code> header, or
        from an <code>event</code> or <code>type</code> field in the body, and files a delivery
        that names neither under <code>default</code>.
      </span>
    </div>
  );
}

// ------------------------------------------------------------ the conditions

const OPERATORS: Array<{ value: PayloadMatchOperator; label: string; needsValue: boolean }> = [
  { value: 'equals', label: 'equals', needsValue: true },
  { value: 'not_equals', label: 'does not equal', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'not_contains', label: 'does not contain', needsValue: true },
  { value: 'in', label: 'is one of', needsValue: true },
  { value: 'not_in', label: 'is none of', needsValue: true },
  { value: 'matches', label: 'matches regex', needsValue: true },
  { value: 'gt', label: '>', needsValue: true },
  { value: 'gte', label: '>=', needsValue: true },
  { value: 'lt', label: '<', needsValue: true },
  { value: 'lte', label: '<=', needsValue: true },
  { value: 'exists', label: 'is present', needsValue: false },
  { value: 'missing', label: 'is absent', needsValue: false },
];

/** A path worth showing per source, so the first condition is not a guess. */
const PATH_EXAMPLES: Record<string, string> = {
  linear: 'data.priority',
  github: 'pull_request.base.ref',
  jira: 'issue.fields.priority.name',
  slack: 'text',
  webhook: 'data.status',
};

function MatchEditor({
  conditions,
  mode,
  source,
  editable,
  onChange,
}: {
  conditions: readonly PayloadMatch[];
  mode: 'all' | 'any';
  source: string;
  editable: boolean;
  onChange(conditions: PayloadMatch[], mode: 'all' | 'any'): void;
}) {
  const set = (index: number, next: PayloadMatch) =>
    onChange(
      conditions.map((condition, position) => (position === index ? next : condition)),
      mode,
    );

  return (
    <div className="field">
      <div className="row">
        <label style={{ flex: 1 }}>Payload conditions</label>
        {conditions.length > 1 && (
          <select
            aria-label="How conditions combine"
            value={mode}
            disabled={!editable}
            onChange={(event) => onChange([...conditions], event.target.value as 'all' | 'any')}
          >
            <option value="all">match all</option>
            <option value="any">match any</option>
          </select>
        )}
      </div>

      {conditions.map((condition, index) => {
        const operator = OPERATORS.find((entry) => entry.value === condition.op);
        return (
          <div className="row wrap" key={index} style={{ alignItems: 'flex-start' }}>
            <input
              aria-label="Payload path"
              style={{ flex: 2, minWidth: '9rem' }}
              value={condition.path}
              placeholder={PATH_EXAMPLES[source] ?? 'data.field'}
              disabled={!editable}
              onChange={(event) => set(index, { ...condition, path: event.target.value })}
            />
            <select
              aria-label="Comparison"
              style={{ flex: 1, minWidth: '8rem' }}
              value={condition.op}
              disabled={!editable}
              onChange={(event) => {
                const op = event.target.value as PayloadMatchOperator;
                const needsValue = OPERATORS.find((entry) => entry.value === op)?.needsValue;
                // Dropping the value when switching to `is present` is not
                // tidiness: the schema rejects a presence test that carries one,
                // so leaving it behind would fail the commit.
                set(index, { ...condition, op, value: needsValue ? condition.value : undefined });
              }}
            >
              {OPERATORS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
            {operator?.needsValue && (
              <input
                aria-label="Value"
                style={{ flex: 2, minWidth: '9rem' }}
                value={formatValue(condition.value)}
                placeholder={condition.op === 'in' || condition.op === 'not_in' ? 'bug, p0' : 'value'}
                disabled={!editable}
                onChange={(event) =>
                  set(index, { ...condition, value: parseValue(event.target.value, condition.op) })
                }
              />
            )}
            {editable && (
              <button
                className="ghost"
                aria-label="Remove condition"
                onClick={() =>
                  onChange(
                    conditions.filter((_, position) => position !== index),
                    mode,
                  )
                }
              >
                ×
              </button>
            )}
          </div>
        );
      })}

      {editable && (
        <div className="row">
          <button
            type="button"
            className="ghost"
            onClick={() =>
              onChange([...conditions, { path: '', op: 'equals', value: '' }], mode)
            }
          >
            <Icon name="plus" /> Add condition
          </button>
        </div>
      )}

      <span className="hint">
        A dotted path into the event payload. <code>*</code> fans out over a list, so{' '}
        <code>labels.*.name</code> reads every label. String comparisons ignore case. Conditions
        run after the event matches, so they narrow rather than widen.
      </span>
    </div>
  );
}

// -------------------------------------------------------- webhook endpoints

/**
 * Mint, list, and revoke the tokens that can POST to this workflow.
 *
 * The token is rendered once, in a banner that says so. Reef stores only a
 * SHA-256 digest, so "show it again" is not a feature that was left out — there
 * is nothing left to show.
 */
function WebhookEndpoints({ workflowId, editable }: { workflowId: string; editable: boolean }) {
  const [endpoints, setEndpoints] = useState<WebhookEndpointSummary[] | null>(null);
  const [issued, setIssued] = useState<{ url: string; token: string; warning?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');

  const reload = () =>
    api
      .listWebhookEndpoints(workflowId)
      .then((result) => setEndpoints(result.endpoints))
      .catch((problem) => setError((problem as Error).message));

  useEffect(() => {
    void reload();
    // The workflow id is the only input; re-running on every render would poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.createWebhookEndpoint(workflowId, name.trim() || undefined);
      setIssued({ url: result.endpoint.url, token: result.token, warning: result.warning });
      setName('');
      await reload();
    } catch (problem) {
      setError((problem as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (endpointId: string) => {
    setBusy(true);
    try {
      await api.revokeWebhookEndpoint(workflowId, endpointId);
      await reload();
    } catch (problem) {
      setError((problem as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const live = (endpoints ?? []).filter((endpoint) => !endpoint.revokedAt);

  return (
    <div className="field">
      <label>Endpoints</label>

      {issued && (
        <div className="banner warn">
          <h3>Copy this token now</h3>
          <p style={{ marginTop: 0 }}>
            It is shown once. Reef stores only a hash of it, so it cannot be shown again — if it is
            lost, revoke this endpoint and create another.
          </p>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            curl -X POST {issued.url} \{'\n'}
            {'  '}-H &quot;Authorization: Bearer {issued.token}&quot; \{'\n'}
            {'  '}-H &quot;Content-Type: application/json&quot; \{'\n'}
            {'  '}-H &quot;X-Reef-Event: deploy.finished&quot; \{'\n'}
            {'  '}-d &apos;{'{"status":"green"}'}&apos;
          </pre>
          <div className="row">
            <button onClick={() => void navigator.clipboard?.writeText(issued.token)}>
              Copy token
            </button>
            <button className="ghost" onClick={() => setIssued(null)}>
              I have saved it
            </button>
          </div>
          {issued.warning && <p style={{ marginBottom: 0 }}>{issued.warning}</p>}
        </div>
      )}

      {error && <p className="hint">{error}</p>}

      {live.length === 0 && endpoints !== null && (
        <span className="hint">No endpoint yet. Create one to get a URL and a token.</span>
      )}

      {live.map((endpoint) => (
        <div className="row" key={endpoint.id}>
          <span className="pill">{endpoint.name}</span>
          <code style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{endpoint.url}</code>
          <span className="hint">
            …{endpoint.tokenHint} ·{' '}
            {endpoint.lastUsedAt
              ? `last used ${new Date(endpoint.lastUsedAt).toLocaleString()}`
              : 'never used'}
          </span>
          {editable && (
            <button className="ghost" disabled={busy} onClick={() => void revoke(endpoint.id)}>
              Revoke
            </button>
          )}
        </div>
      ))}

      {editable && (
        <div className="row">
          <input
            aria-label="Endpoint name"
            placeholder="What will call this? e.g. Deploy pipeline"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <button className="primary" disabled={busy} onClick={() => void create()}>
            Create endpoint
          </button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- token pickers

/**
 * Pick from a list, or type what the list does not know about.
 *
 * The narrowing fields these replace were plain comma-separated text, and they
 * still are underneath: a deployment with no Linear connection or no Slack bot
 * token has no list to offer, and locking those people out of a field that used
 * to work is a worse outcome than a picker that sometimes falls back. So an
 * unfetchable list is not an error here — it leaves a text box with a hint.
 *
 * Selected entries are chips rather than a comma-joined string, because the
 * stored value (a team key, a channel id) is not always the readable one, and a
 * chip can show the name while holding the id.
 */
function TokenPicker({
  id,
  label,
  values,
  options,
  reason,
  placeholder,
  fallbackHint,
  commitOnBlur = false,
  editable,
  onChange,
}: {
  id: string;
  label: string;
  values: readonly string[];
  options: readonly PickerOption[];
  reason: string | null;
  placeholder: string;
  /** What to say when there is no list — it has to name what to type instead. */
  fallbackHint: ReactNode;
  /**
   * Take what was typed when focus leaves, rather than dropping it.
   *
   * Off for a list the picker can search — `eng` on the way to Engineering
   * is a search, not a value. On for a field whose value is usually typed
   * from memory, where the half-typed word *is* the answer and losing it on
   * a click elsewhere reads as the field refusing to save.
   */
  commitOnBlur?: boolean;
  editable: boolean;
  onChange(values: string[]): void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  // A click anywhere else closes the list. Without it the options stay drawn
  // over the fields below them after focus has moved on.
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return options
      .filter((option) => !values.includes(option.value))
      .filter(
        (option) =>
          !needle ||
          option.label.toLowerCase().includes(needle) ||
          option.value.toLowerCase().includes(needle),
      )
      .slice(0, 8);
  }, [options, values, query]);

  const add = (value: string) => {
    const entry = value.trim();
    if (!entry || values.includes(entry)) return;
    onChange([...values, entry]);
    setQuery('');
    setActive(0);
  };

  const remove = (value: string) => onChange(values.filter((entry) => entry !== value));

  /** What to call a stored value on its chip: its name, when the list knows it. */
  const describe = (value: string) =>
    options.find((option) => option.value === value)?.label ?? value;
  const glyphOf = (value: string) => options.find((option) => option.value === value)?.glyph;

  return (
    <div className="field" style={{ flex: 1, minWidth: '10rem' }} ref={boxRef}>
      <label htmlFor={id}>{label}</label>

      {values.length > 0 && (
        <div className="row wrap" style={{ gap: '0.25rem' }}>
          {values.map((value) => (
            <span className="pill" key={value} title={value}>
              {glyphOf(value) && <span className="emoji-glyph">{glyphOf(value)}</span>}
              {describe(value)}
              {editable && (
                <button
                  className="chip-x"
                  aria-label={`Remove ${describe(value)}`}
                  onClick={() => remove(value)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="combo">
        <input
          id={id}
          role="combobox"
          aria-expanded={open && matches.length > 0}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          autoComplete="off"
          value={query}
          placeholder={values.length ? 'add another' : placeholder}
          disabled={!editable}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              if (!matches.length) return;
              const step = event.key === 'ArrowDown' ? 1 : matches.length - 1;
              setActive((index) => (index + step) % matches.length);
              return;
            }
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              // Nothing highlighted means the list has no answer for what was
              // typed — an id, or a workspace reef cannot enumerate. Take it as
              // typed rather than swallowing the keystroke.
              add(open && matches[active] ? matches[active].value : query);
              return;
            }
            if (event.key === 'Escape') setOpen(false);
            // Backspace on an empty box removes the chip before the caret,
            // which is what every other token input does.
            const last = values[values.length - 1];
            if (event.key === 'Backspace' && !query && last) remove(last);
          }}
          // Leaving abandons what was half-typed rather than adding it unless
          // the field says otherwise: `eng` on the way to picking Engineering
          // is a search, not a value. Enter and comma always commit.
          onBlur={() => {
            if (commitOnBlur && query.trim()) add(query);
            setQuery('');
            setOpen(false);
          }}
        />

        {open && matches.length > 0 && (
          <ul className="combo-list" id={`${id}-list`} role="listbox">
            {matches.map((option, index) => (
              <li key={option.value}>
                <button
                  role="option"
                  aria-selected={index === active}
                  className={index === active ? 'active' : ''}
                  // `mousedown`, because the input's blur would otherwise clear
                  // the query and re-render the list out from under the click.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    add(option.value);
                  }}
                  onMouseEnter={() => setActive(index)}
                >
                  {option.glyph && <span className="emoji-glyph">{option.glyph}</span>}
                  <span>{option.label}</span>
                  {option.hint && <code>{option.hint}</code>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {reason && (
        <span className="hint">
          {fallbackHint} ({reason})
        </span>
      )}
    </div>
  );
}

interface PickerOption {
  /** What the trigger stores. */
  value: string;
  /** What the option and its chip read as. */
  label: string;
  /** A short second column, e.g. the team key behind the team name. */
  hint?: string;
  /** The character itself, for an emoji: what the name looks like in Slack. */
  glyph?: string;
}

/**
 * Fetch a picker's list once per page rather than once per trigger.
 *
 * Two Linear triggers on one workflow is ordinary, and each rendering its own
 * picker would otherwise mean two identical round trips to the provider.
 */
const listCache = new Map<string, Promise<{ options: PickerOption[]; reason: string | null }>>();

function useRemoteOptions(
  key: string,
  load: () => Promise<{ options: PickerOption[]; reason: string | null }>,
): { options: PickerOption[]; reason: string | null } {
  const [state, setState] = useState<{ options: PickerOption[]; reason: string | null }>({
    options: [],
    reason: null,
  });

  useEffect(() => {
    let live = true;
    let pending = listCache.get(key);
    if (!pending) {
      pending = load().catch((problem) => ({ options: [], reason: (problem as Error).message }));
      listCache.set(key, pending);
    }
    void pending.then((result) => live && setState(result));
    return () => {
      live = false;
    };
    // `load` is a fresh closure every render; the key is what identifies the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

/**
 * Teams to narrow a Linear trigger to.
 *
 * A picked team stores its **key** — `ENG` — because that is what the webhook
 * classifier emits alongside the id, and it is the half a person can read in a
 * committed workflow.
 */
function LinearTeamPicker({
  values,
  editable,
  onChange,
}: {
  values: readonly string[];
  editable: boolean;
  onChange(values: string[]): void;
}) {
  const { options, reason } = useRemoteOptions('linear-teams', async () => {
    const result = await api.listLinearTeams();
    return {
      options: result.teams.map((team) => ({ value: team.key, label: team.name, hint: team.key })),
      reason: result.reason ?? null,
    };
  });

  return (
    <TokenPicker
      id="linear-teams"
      label="Teams"
      values={values}
      options={options}
      reason={reason}
      placeholder={options.length ? 'search teams' : 'any'}
      fallbackHint={
        <>
          Teams could not be listed — type a team key such as <code>ENG</code>
        </>
      }
      editable={editable}
      onChange={onChange}
    />
  );
}

/**
 * Channels to narrow a Slack trigger to.
 *
 * The list is the channels the bot is in, which is also the set a Slack trigger
 * can ever fire in — a channel the bot was never invited to would be a choice
 * that silently never matches. The stored value is the channel id, because that
 * is what the event carries.
 */
function SlackChannelPicker({
  values,
  editable,
  onChange,
}: {
  values: readonly string[];
  editable: boolean;
  onChange(values: string[]): void;
}) {
  const { options, reason } = useRemoteOptions('slack-channels', async () => {
    const result = await api.listSlackChannels();
    return {
      options: result.channels.map((channel) => ({
        value: channel.id,
        label: `${channel.private ? '🔒' : '#'}${channel.name}`,
        hint: channel.id,
      })),
      reason: result.reason ?? null,
    };
  });

  return (
    <TokenPicker
      id="slack-channels"
      label="Channels"
      values={values}
      options={options}
      reason={reason}
      placeholder={options.length ? 'search channels' : 'any'}
      fallbackHint={
        <>
          Channels could not be listed — type a channel id such as <code>C0123456789</code>
        </>
      }
      editable={editable}
      onChange={onChange}
    />
  );
}

/**
 * The reactions a Slack trigger listens for.
 *
 * Slack names a reaction rather than sending the character — `rocket`, not
 * 🚀 — and there is no API that lists the standard set, so the list here is
 * the names people reach for, drawn with the character so nobody has to know
 * that 👍 is `+1`. Anything not on it can still be typed, and the field is
 * the one picker that keeps a typed name when focus leaves: it is usually
 * typed from memory in full, and a name that vanished on the way to the Save
 * button read as the emoji not being accepted.
 */
function EmojiPicker({
  values,
  applies,
  editable,
  onChange,
}: {
  values: readonly string[];
  /** Whether "Reaction added" is on — without it the filter narrows nothing. */
  applies: boolean;
  editable: boolean;
  onChange(values: string[]): void;
}) {
  return (
    <div className="field" style={{ flex: 1, minWidth: '12rem' }}>
      <TokenPicker
        id="slack-emoji"
        label="Reaction emoji"
        values={values}
        options={SLACK_EMOJI}
        reason={null}
        placeholder="search, or type a name like rocket"
        fallbackHint={null}
        commitOnBlur
        editable={editable}
        onChange={onChange}
      />
      <span className="hint">
        {applies
          ? 'Pick from the list or type the name Slack uses. A typed name is kept when you press Enter or click away; leave this empty to run on any reaction.'
          : 'Only used by “Reaction added”, which is not switched on above.'}
      </span>
    </div>
  );
}

const SLACK_EMOJI: readonly PickerOption[] = (
  [
    ['rocket', '🚀'],
    ['white_check_mark', '✅'],
    ['eyes', '👀'],
    ['+1', '👍'],
    ['-1', '👎'],
    ['fire', '🔥'],
    ['tada', '🎉'],
    ['bug', '🐛'],
    ['warning', '⚠️'],
    ['x', '❌'],
    ['heavy_check_mark', '✔️'],
    ['question', '❓'],
    ['exclamation', '❗'],
    ['memo', '📝'],
    ['mag', '🔍'],
    ['hammer_and_wrench', '🛠️'],
    ['wrench', '🔧'],
    ['gear', '⚙️'],
    ['ship', '🚢'],
    ['pushpin', '📌'],
    ['bookmark', '🔖'],
    ['bell', '🔔'],
    ['hourglass', '⏳'],
    ['star', '⭐'],
    ['heart', '❤️'],
    ['thinking_face', '🤔'],
    ['raised_hands', '🙌'],
    ['pray', '🙏'],
    ['wave', '👋'],
    ['robot_face', '🤖'],
    ['ticket', '🎫'],
    ['package', '📦'],
    ['label', '🏷️'],
    ['lock', '🔒'],
    ['key', '🔑'],
    ['red_circle', '🔴'],
    ['large_green_circle', '🟢'],
    ['large_yellow_circle', '🟡'],
    ['rotating_light', '🚨'],
    ['sos', '🆘'],
    ['construction', '🚧'],
    ['recycle', '♻️'],
    ['repeat', '🔁'],
    ['arrow_up', '⬆️'],
    ['chart_with_upwards_trend', '📈'],
    ['speech_balloon', '💬'],
    ['inbox_tray', '📥'],
    ['zap', '⚡'],
    ['sparkles', '✨'],
    ['100', '💯'],
    ['ok_hand', '👌'],
  ] as const
).map(([name, glyph]) => ({ value: name, label: name, glyph }));

/**
 * What the Slack trigger will do, as one sentence.
 *
 * The three events and two filters combine into a rule that is easy to build
 * and hard to read back from the fields — "reaction_added, channels C012,
 * emoji rocket" says nothing about the message the reaction has to be on.
 * The sentence is the same rule in the order it happens.
 */
function describeSlack(events: readonly string[], channels: readonly string[], emoji: readonly string[]): string {
  if (events.length === 0) return 'Pick at least one event above, or this trigger never fires.';
  const where =
    channels.length === 0
      ? 'any channel the reef bot is in'
      : channels.length === 1
        ? 'the chosen channel'
        : `any of the ${channels.length} chosen channels`;
  const parts = events.map((event) => {
    switch (event) {
      case 'app_mention':
        return `someone @-mentions the reef bot in ${where}`;
      case 'reaction_added': {
        const which =
          emoji.length === 0
            ? 'any emoji'
            : emoji.map((name) => `:${name}:`).join(' or ');
        return `someone reacts with ${which} to a message in ${where}`;
      }
      case 'slash_command':
        return 'someone runs /reef run with this workflow’s id';
      default:
        return event;
    }
  });
  return `Runs when ${parts.join(', or when ')}.`;
}

// -------------------------------------------------------------------- shared

function ListField({
  label,
  values,
  placeholder,
  editable,
  onChange,
}: {
  label: string;
  values: readonly string[];
  placeholder?: string;
  editable: boolean;
  onChange(values: string[]): void;
}) {
  return (
    <div className="field" style={{ flex: 1, minWidth: '10rem' }}>
      <label>{label}</label>
      <input
        aria-label={label}
        value={values.join(', ')}
        placeholder={placeholder ?? 'any'}
        disabled={!editable}
        onChange={(event) => onChange(splitList(event.target.value))}
      />
    </div>
  );
}

const SOURCE_LABELS: Record<TriggerSpec['type'], string> = {
  manual: 'Manual',
  schedule: 'Schedule',
  webhook: 'Webhook',
  linear: 'Linear',
  github: 'GitHub',
  jira: 'Jira',
  slack: 'Slack',
};

/** When each source fires, in the words a person choosing one needs. */
const SOURCE_BLURBS: Record<TriggerSpec['type'], string> = {
  manual: 'Someone presses Run.',
  schedule: 'On a cron schedule, in UTC.',
  webhook: 'Something POSTs to a URL reef gives you, with a token.',
  linear: 'An issue is created or changes in Linear.',
  github: 'A pull request or issue changes on GitHub.',
  jira: 'An issue is created or changes in Jira.',
  slack: 'The reef bot is mentioned, a reaction is added, or a slash command is run.',
};

function blankTrigger(type: TriggerSpec['type']): TriggerSpec {
  switch (type) {
    case 'manual':
      return { type: 'manual', triggerFromEveryone: false };
    case 'schedule':
      return { type: 'schedule', cron: '0 9 * * 1-5' };
    case 'webhook':
      return { type: 'webhook', events: [] };
    case 'linear':
      return { type: 'linear', events: ['issue.created'], labels: [], projects: [], teams: [] };
    case 'github':
      return { type: 'github', events: ['pull_request.opened'], repos: [], branches: [], labels: [] };
    case 'jira':
      return {
        type: 'jira',
        events: ['issue.assigned'],
        projects: [],
        labels: [],
        issueTypes: [],
        assignees: [],
      };
    case 'slack':
      return { type: 'slack', events: ['app_mention'], channels: [], emoji: [] };
  }
}

function splitList(text: string): string[] {
  return text
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/**
 * Turn what someone typed into the value the condition stores.
 *
 * `in`/`not_in` take a list, so a comma splits. Everything else takes a scalar,
 * and `true`/`42` are kept as a boolean and a number rather than their strings
 * — the matcher bridges the two anyway, but a committed document reads better
 * when it says what was meant.
 */
function parseValue(text: string, op: PayloadMatchOperator): unknown {
  if (op === 'in' || op === 'not_in') return splitList(text);
  if (text === 'true') return true;
  if (text === 'false') return false;
  if (text.trim() !== '' && Number.isFinite(Number(text))) return Number(text);
  return text;
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (value === undefined || value === null) return '';
  return String(value);
}
